import React, { useEffect, useState } from "react";
import Header from "../components/Header";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { UsersThree, MagnifyingGlass, Clock, Trash } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { fmtDuration } from "../lib/duration";

function fmtDate(iso) {
    if (!iso) return "—";
    return new Date(iso).toLocaleString(undefined, { hour12: false });
}

function fmtMinutes(m) {
    return fmtDuration(m);
}

export default function Members() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [tab, setTab] = useState("directory");
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [usage, setUsage] = useState([]);
    const [usageLoading, setUsageLoading] = useState(false);
    const [usageLoaded, setUsageLoaded] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/users");
                setUsers(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const deleteUser = async (u) => {
        if (!window.confirm(
            `Delete account for ${u.name} (${u.company_number})?\n\n` +
            `This is permanent — the user will need to re-register. Recorded vessel data they submitted will remain in history.`
        )) return;
        try {
            await api.delete(`/users/${u.id}`);
            toast.success(`${u.name} deleted`);
            setUsers((prev) => prev.filter((x) => x.id !== u.id));
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => {
        if (tab === "usage" && isAdmin && !usageLoaded) {
            setUsageLoading(true);
            (async () => {
                try {
                    const { data } = await api.get("/admin/usage");
                    setUsage(Array.isArray(data) ? data : []);
                    setUsageLoaded(true);
                } catch (e) {
                    toast.error(formatApiError(e));
                } finally {
                    setUsageLoading(false);
                }
            })();
        }
    }, [tab, isAdmin, usageLoaded]);

    const query = q.trim().toLowerCase();
    const filtered = query
        ? users.filter(
              (u) =>
                  u.name.toLowerCase().includes(query) ||
                  u.position.toLowerCase().includes(query) ||
                  u.company_number.includes(query) ||
                  u.role.includes(query)
          )
        : users;

    const approved = filtered.filter((u) => u.status === "approved");
    const byRole = {
        admin: approved.filter((u) => u.role === "admin"),
        manager: approved.filter((u) => u.role === "manager"),
        supervisor: approved.filter((u) => u.role === "supervisor"),
    };

    return (
        <div className="min-h-screen">
            <Header />
            <main className="max-w-[1200px] mx-auto px-5 py-8">
                <div className="mb-6 flex items-end justify-between gap-4 flex-wrap">
                    <div>
                        <div className="uppercase-label text-[var(--grindrod-red)]">Directory</div>
                        <h1 className="font-display font-black text-3xl text-[var(--grindrod-navy)]">
                            Members · Richards Bay
                        </h1>
                        <p className="text-slate-500 text-sm mt-1">
                            All active personnel with access to the Live Vessel Loading Tracker.
                        </p>
                    </div>
                    {tab === "directory" && (
                        <div className="relative">
                            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                            <input
                                value={q}
                                onChange={(e) => setQ(e.target.value)}
                                placeholder="Search name, number, position…"
                                className="g-input pl-9"
                                style={{ minWidth: 280 }}
                                data-testid="members-search"
                            />
                        </div>
                    )}
                </div>

                {isAdmin && (
                    <div className="flex gap-2 mb-5 border-b border-slate-200">
                        <TabBtn active={tab === "directory"} onClick={() => setTab("directory")} testid="tab-directory">
                            <UsersThree size={14} /> Directory
                        </TabBtn>
                        <TabBtn active={tab === "usage"} onClick={() => setTab("usage")} testid="tab-usage">
                            <Clock size={14} /> Usage Tracking
                        </TabBtn>
                    </div>
                )}

                {tab === "directory" && (
                    <>
                        <div className="grid grid-cols-3 gap-4 mb-6">
                            <KpiTile color="#E3000F" label="Admins" value={byRole.admin.length} />
                            <KpiTile color="#0B1B3D" label="Managers" value={byRole.manager.length} />
                            <KpiTile color="#00A344" label="Supervisors" value={byRole.supervisor.length} />
                        </div>

                        {loading ? (
                            <div className="text-slate-500" data-testid="members-loading">Loading…</div>
                        ) : approved.length === 0 ? (
                            <div className="g-card p-10 text-center" data-testid="members-empty">
                                <UsersThree size={44} weight="duotone" className="mx-auto text-slate-300 mb-3" />
                                <div className="font-display font-bold text-xl text-[var(--grindrod-navy)]">No members found</div>
                            </div>
                        ) : (
                            <div className="g-card overflow-x-auto">
                                <table className="g-table" data-testid="members-table">
                                    <thead>
                                        <tr>
                                            <th>Name</th>
                                            <th>Position</th>
                                            <th>Company #</th>
                                            <th>Role</th>
                                            <th>Joined</th>
                                            {isAdmin && <th className="text-right">Actions</th>}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {approved.map((u) => (
                                            <tr key={u.id} data-testid={`member-row-${u.id}`}>
                                                <td className="font-semibold text-[var(--grindrod-navy)]">{u.name}</td>
                                                <td>{u.position}</td>
                                                <td className="tabular">{u.company_number}</td>
                                                <td>
                                                    <span
                                                        className={`g-pill ${
                                                            u.role === "admin"
                                                                ? "g-pill-red"
                                                                : u.role === "manager"
                                                                ? "g-pill-navy"
                                                                : "g-pill-green"
                                                        }`}
                                                    >
                                                        {u.role}
                                                    </span>
                                                </td>
                                                <td className="text-xs text-slate-500">
                                                    {new Date(u.created_at).toLocaleDateString()}
                                                </td>
                                                {isAdmin && (
                                                    <td className="text-right">
                                                        {u.role !== "admin" && u.id !== user.id ? (
                                                            <button
                                                                onClick={() => deleteUser(u)}
                                                                className="btn-outline !py-1 !px-2 text-[11px] !border-[var(--grindrod-red)] !text-[var(--grindrod-red)] inline-flex items-center gap-1"
                                                                data-testid={`delete-user-${u.id}`}
                                                                title={`Delete ${u.name}`}
                                                            >
                                                                <Trash size={11} weight="bold" /> Delete
                                                            </button>
                                                        ) : (
                                                            <span className="text-[10px] text-slate-400">—</span>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}

                {tab === "usage" && isAdmin && (
                    <div className="g-card overflow-x-auto" data-testid="usage-panel">
                        <div className="g-card-header-dark">
                            <Clock size={18} />
                            <div className="font-display font-bold uppercase tracking-wide">Platform Usage · All Users</div>
                        </div>
                        {usageLoading ? (
                            <div className="p-6 text-center text-sm text-slate-400" data-testid="usage-loading">Loading usage…</div>
                        ) : usage.length === 0 ? (
                            <div className="p-6 text-center text-sm text-slate-400" data-testid="usage-empty">
                                No session data captured yet.
                            </div>
                        ) : (
                            <table className="g-table" data-testid="usage-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Role</th>
                                        <th className="text-right">Total time</th>
                                        <th className="text-right">Sessions</th>
                                        <th>Last seen</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usage.map((u) => (
                                        <tr key={u.user_id} data-testid={`usage-row-${u.user_id}`}>
                                            <td className="font-semibold text-[var(--grindrod-navy)]">{u.user_name}</td>
                                            <td>
                                                <span
                                                    className={`g-pill ${
                                                        u.user_role === "admin"
                                                            ? "g-pill-red"
                                                            : u.user_role === "manager"
                                                            ? "g-pill-navy"
                                                            : "g-pill-green"
                                                    }`}
                                                >
                                                    {u.user_role}
                                                </span>
                                            </td>
                                            <td className="tabular text-right">{fmtMinutes(u.total_minutes)}</td>
                                            <td className="tabular text-right">{u.sessions}</td>
                                            <td className="text-xs text-slate-500">{fmtDate(u.last_seen)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

function KpiTile({ color, label, value }) {
    return (
        <div className="g-card p-4" data-testid={`kpi-${label.toLowerCase()}`} style={{ borderTop: `3px solid ${color}` }}>
            <div className="uppercase-label text-[10px]">{label}</div>
            <div className="font-display font-black text-3xl text-[var(--grindrod-navy)] mt-1 tabular">{value}</div>
        </div>
    );
}

function TabBtn({ active, onClick, children, testid }) {
    return (
        <button
            onClick={onClick}
            data-testid={testid}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-semibold uppercase tracking-wide transition-colors ${
                active
                    ? "text-[var(--grindrod-navy)] border-b-2 border-[var(--grindrod-red)]"
                    : "text-slate-500 hover:text-[var(--grindrod-navy)]"
            }`}
        >
            {children}
        </button>
    );
}
